import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const TYPE_LABEL = {
  cash_payment: "Cash payment",
  call_limit_purchase: "Call-limit purchase",
  tts_charge: "TTS",
  ai_model_charge: "AI Model",
  adjustment: "Adjustment",
  renewal_charge: "Renewal charge",
  refund: "Refund",
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

/**
 * Admin / reseller modal: manually add or deduct cash balance and view history.
 * `account` must include { id, name, cash_balance_minor?, type? }.
 */
export default function AdjustBalanceModal({ account, open, onClose, onSaved }) {
  const [mode, setMode] = useState("add"); // add | deduct
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [balance, setBalance] = useState(0);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!account?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get("/billing/transactions", { params: { account_id: account.id } });
      setTxns(data || []);
      const last = (data || [])[0];
      if (last && typeof last.balance_after_minor === "number") {
        setBalance(last.balance_after_minor);
      } else if (account.cash_balance_minor != null) {
        setBalance(account.cash_balance_minor);
      } else {
        setBalance(0);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [account]);

  useEffect(() => {
    if (!open || !account?.id) return;
    setMode("add");
    setAmount("");
    setNote("");
    setBalance(account.cash_balance_minor || 0);
    load();
  }, [open, account, load]);

  const submit = async () => {
    const dollars = parseFloat(amount);
    if (!dollars || dollars <= 0) {
      toast.error("Enter a valid amount greater than 0");
      return;
    }
    const minor = Math.round(dollars * 100);
    const signed = mode === "add" ? minor : -minor;
    setSaving(true);
    try {
      const { data } = await api.post("/billing/adjust", {
        account_id: account.id,
        amount_minor: signed,
        description: note.trim() || undefined,
      });
      toast.success(mode === "add"
        ? `Added ${money(minor)}`
        : `Deducted ${money(minor)}`);
      setAmount("");
      setNote("");
      setBalance(data.cash_balance_minor ?? data.balance_after_minor ?? 0);
      await load();
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!account) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="adjust-balance-dialog">
        <DialogHeader>
          <DialogTitle>Balance — {account.name || account.company || "Account"}</DialogTitle>
          <DialogDescription>Manually add or deduct cash balance. Every change is recorded in history.</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Available balance</p>
            <p className="text-2xl font-semibold tabular-nums" data-testid="adjust-balance-current">
              {money(balance)}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex gap-2">
            <Button type="button" size="sm" variant={mode === "add" ? "default" : "outline"}
              className="rounded-lg flex-1" onClick={() => setMode("add")} data-testid="adjust-mode-add">
              Add
            </Button>
            <Button type="button" size="sm" variant={mode === "deduct" ? "default" : "outline"}
              className="rounded-lg flex-1" onClick={() => setMode("deduct")} data-testid="adjust-mode-deduct">
              Deduct
            </Button>
          </div>
          <div className="space-y-1.5">
            <Label>Amount (USD)</Label>
            <Input data-testid="adjust-amount" type="number" min="0.01" step="0.01"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder={mode === "add" ? "100.00" : "25.00"} />
          </div>
          <div className="space-y-1.5">
            <Label>Note <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
            <Input data-testid="adjust-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={mode === "add" ? "Manual top-up" : "Manual deduction"} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-lg" onClick={onClose}>Close</Button>
          <Button className="rounded-lg" onClick={submit} disabled={saving} data-testid="adjust-submit-btn">
            {saving ? "Saving…" : (mode === "add" ? "Add balance" : "Deduct balance")}
          </Button>
        </DialogFooter>

        <div className="border-t border-border pt-3" data-testid="adjust-history">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Transaction history</p>
          {loading ? (
            <p className="text-sm text-muted-foreground py-2">Loading…</p>
          ) : txns.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No transactions yet.</p>
          ) : (
            <div className="rounded-md border border-border overflow-hidden max-h-56 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">After</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {txns.map((t) => {
                    const credit = t.amount_minor >= 0;
                    return (
                      <TableRow key={t.id}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(t.created_at)}</TableCell>
                        <TableCell className="text-xs" title={t.description || ""}>
                          {TYPE_LABEL[t.txn_type] || t.txn_type}
                        </TableCell>
                        <TableCell className={`text-right tabular-nums text-xs font-semibold ${credit ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                          {credit ? "+" : "−"}{money(Math.abs(t.amount_minor))}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{money(t.balance_after_minor)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
